import { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PageHeader from '../components/ui/PageHeader';
import { FadeIn } from '../components/ui/AnimatedSection';
import { supabase } from '../lib/supabase';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Recrutement() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    motivation: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name || !formData.email || !formData.phone || !formData.experience) {
      setFormError(t('recrutement.errorIncomplete'));
      return;
    }

    setLoading(true);

    try {
      if (!supabase) throw new Error("Supabase is not configured.");

      const { error } = await supabase.from('applications').insert([formData]);
      
      if (error) throw error;
      setSuccess(true);
    } catch (err) {
      console.error('Error submitting application:', err);
      alert(t('recrutement.errorAlert'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-sans text-black-deep bg-cream-100">
      <Navbar />
      <PageHeader title={t('recrutement.heroTitle')} subtitle={t('recrutement.heroSubtitle')} imagePath="/assets/luxury_gloves.webp" />

      <main className="flex-grow py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="bg-white p-8 md:p-12 shadow-2xl border border-gold-200/50 rounded-sm">
              {success ? (
                <div className="flex flex-col items-center justify-center text-center py-12">
                  <CheckCircle2 className="w-20 h-20 text-gold-500 mb-6" />
                  <h3 className="text-2xl font-serif text-black-deep mb-2">{t('recrutement.successTitle')}</h3>
                  <p className="text-gray-500 font-light">{t('recrutement.successDesc')}</p>
                </div>
              ) : (
                <>
                  <h2 className="text-3xl font-serif text-black-deep mb-4 text-center">{t('recrutement.formTitle')}</h2>
                  <p className="text-gray-500 text-center mb-10 font-light">{t('recrutement.formDesc')}</p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="rec-name" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('recrutement.labelName')}</label>
                      <input id="rec-name" required type="text" className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
                        value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <label htmlFor="rec-phone" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('recrutement.labelPhone')}</label>
                      <input id="rec-phone" required type="tel" className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
                        value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="rec-email" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('recrutement.labelEmail')}</label>
                    <input id="rec-email" required type="email" className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
                      value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                    />
                  </div>

                  <div>
                    <label htmlFor="rec-exp" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('recrutement.labelExperience')}</label>
                    <select id="rec-exp" className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
                      value={formData.experience} onChange={e => setFormData({...formData, experience: e.target.value})}
                    >
                      <option value="">{t('recrutement.expSelect')}</option>
                      <option value="1-3">{t('recrutement.exp1')}</option>
                      <option value="3-5">{t('recrutement.exp2')}</option>
                      <option value="5+">{t('recrutement.exp3')}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="rec-motivation" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('recrutement.labelMotivation')}</label>
                    <textarea id="rec-motivation" rows={4} className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
                      value={formData.motivation} onChange={e => setFormData({...formData, motivation: e.target.value})}
                    ></textarea>
                  </div>

                  {formError && (
                    <div className="text-red-600 text-sm font-bold bg-red-50 border border-red-200 p-4 rounded-sm text-center">
                      {formError}
                    </div>
                  )}

                  <div className="pt-4">
                    <button disabled={loading} type="submit" className="w-full flex items-center justify-center bg-gradient-to-r from-black-deep to-gray-900 text-white uppercase tracking-widest text-sm py-4 rounded-full shadow-[0_8px_20px_rgba(10,10,10,0.3)] hover:shadow-[0_12px_25px_rgba(10,10,10,0.5)] hover:-translate-y-0.5 transition-all duration-300 mt-4 disabled:opacity-70 disabled:cursor-not-allowed">
                      {loading ? <Loader2 className="animate-spin mr-2" /> : null}
                      {loading ? t('recrutement.btnLoading') : t('recrutement.btnSubmit')}
                    </button>
                  </div>
                </form>
                </>
              )}
            </div>
          </FadeIn>
        </div>
      </main>
      <Footer />
    </div>
  );
}
